import { cn } from '@/lib/utils';

/**
 * Google Ads Quality Score pill (1-10, from ad_group_criterion.quality_info).
 * Bands match the team's report convention: 1-3 red (action needed), 4-6 amber
 * (could improve), 7-10 green (healthy). Null renders an em-dash placeholder —
 * QS only exists for keywords with enough recent impressions.
 *
 * Extracted from performance-v2 (#5847) so the ad Keywords tab and any future
 * quality surfaces share one banding.
 *
 * With `subScores`, hovering the pill opens Google's three QS components
 * (expected CTR, ad relevance, landing page experience) as coloured
 * bucket rows — the "why" behind the number, so a weak score points at
 * what to fix. Bucket values are QualityScoreBucket enum names.
 */

export interface QualitySubScores {
  expected_ctr?: string | null;
  ad_relevance?: string | null;
  landing_page_experience?: string | null;
}

const BUCKETS: Record<string, { label: string; dot: string }> = {
  BELOW_AVERAGE: { label: 'Below average', dot: 'bg-red-500' },
  AVERAGE: { label: 'Average', dot: 'bg-amber-500' },
  ABOVE_AVERAGE: { label: 'Above average', dot: 'bg-green-500' },
};

const SubScoreRow = ({ name, bucket }: { name: string; bucket?: string | null }) => {
  const b = bucket ? BUCKETS[bucket] : undefined;
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-gray-500">{name}</span>
      {b ? (
        <span className="flex items-center gap-1.5 text-gray-900">
          <span className={cn('h-2 w-2 rounded-full', b.dot)} />
          {b.label}
        </span>
      ) : (
        <span className="text-gray-400">—</span>
      )}
    </div>
  );
};

export function QualityScorePill({ score, subScores }: { score: number | null | undefined; subScores?: QualitySubScores }) {
  if (score == null) return <span className="text-xs text-gray-400">—</span>;
  const cls =
    score <= 3 ? 'bg-red-100 text-red-700' :
    score <= 6 ? 'bg-amber-100 text-amber-800' :
    'bg-green-100 text-green-800';
  const pill = (
    <span className={cn('inline-flex items-center justify-center w-7 h-5 rounded-full text-xs font-semibold', cls)}>
      {score}
    </span>
  );
  const hasSubScores = !!subScores && (subScores.expected_ctr || subScores.ad_relevance || subScores.landing_page_experience);
  if (!hasSubScores) return pill;
  return (
    <span className="group relative inline-flex">
      {pill}
      {/* Right-anchored so a pill at the table edge grows the panel inward. */}
      <span className="pointer-events-none absolute right-0 top-full z-20 mt-1.5 hidden w-60 rounded-lg border border-gray-200 bg-white p-3 text-left text-xs shadow-lg group-hover:block">
        <span className="mb-2 block font-semibold text-gray-900">Quality Score {score}/10</span>
        <span className="block space-y-1.5">
          <SubScoreRow name="Expected CTR" bucket={subScores?.expected_ctr} />
          <SubScoreRow name="Ad relevance" bucket={subScores?.ad_relevance} />
          <SubScoreRow name="Landing page" bucket={subScores?.landing_page_experience} />
        </span>
      </span>
    </span>
  );
}
